import React, { useMemo, useState } from 'react';
import atatus from 'atatus';
import { UNAUTHORIZED_EXCEPTION } from 'constant/index';
import PropTypes from 'prop-types';
import { tagForm } from 'tagManager';

import useAuth from '../../context/useAuth';
import scrollTo from '../../utils/scrollTo';
import ErrorView from '../errors/ErrorView';
import loginErrors from '../errors/loginErrors';
import OverlaySpinner from '../spinner/OverlaySpinner';
import { PasswordInput, PrimaryButtonSto, TextInputSto } from '../sto';

const LoginForm = (props) => {
    const { isFetchingUser, signIn } = useAuth();
    const { password, setFormField, username } = props;

    const [error, setError] = useState(null);

    const isFormValid = useMemo(() => username.isValid && password.isValid, [username, password]);

    const handleInputChange = (event) => {
        const fieldName = event.target.name;
        let value;
        let isValid;
        switch (fieldName) {
            case 'username':
                value = event.target.value.replace(/[^\w@_.]/g, '');
                isValid = value.length >= 1 ? true : value.length < 1 && username.isValid !== null ? false : null;
                setFormField(fieldName, { isValid, value });
                break;
            case 'password':
                ({ value } = event.target);
                isValid = value.length >= 1 ? true : value.length < 1 && password.isValid !== null ? false : null;
                setFormField(fieldName, { isValid, value });
                break;
            default:
                break;
        }
    };

    const handleSubmitClick = async (e) => {
        e.preventDefault();
        scrollTo(0, 0);
        setError(null);
        try {
            await signIn(username.value, password.value);
        } catch (e) {
            let errorMessage;
            if (e.code === UNAUTHORIZED_EXCEPTION) {
                errorMessage = loginErrors[e.message];
            } else {
                errorMessage = e.code ? loginErrors[e.code] : loginErrors[e.message];
            }
            setError(errorMessage || 'general.unexpectedErrorOccurred');

            performance.measure('authentication-failed', 'login');
            const [{ duration }] = performance.getEntriesByName('authentication-failed', 'measure');
            atatus.recordTransaction('Login', duration, false);

            tagForm({
                fields: { username: username.value },
                message: e.message,
                name: 'login',
                success: false,
            });
        }
    };

    return (
        <div>
            {isFetchingUser && <OverlaySpinner />}
            <ErrorView error={error} />
            <form onSubmit={handleSubmitClick}>
                <TextInputSto
                    errorMessage="pages.login.username.field.error"
                    isValid={username.isValid}
                    label="pages.login.username.field"
                    name="username"
                    onChange={handleInputChange}
                    placeholder="pages.login.username.placeholder"
                    value={username.value}
                />

                <PasswordInput
                    errorMessage="pages.login.password.field.error"
                    isValid={password.isValid}
                    label="pages.login.password.field"
                    name="password"
                    onChange={handleInputChange}
                    placeholder="pages.login.password.placeholder"
                    value={password.value}
                />

                <PrimaryButtonSto
                    disabled={!isFormValid}
                    labelKey="pages.login.connect"
                    onClick={handleSubmitClick}
                    type="submit"
                />
            </form>
        </div>
    );
};

LoginForm.propTypes = {
    password: PropTypes.exact({
        errorMessage: PropTypes.string,
        isValid: PropTypes.bool,
        value: PropTypes.string.isRequired,
    }).isRequired,
    setFormField: PropTypes.func.isRequired,
    username: PropTypes.exact({
        errorMessage: PropTypes.string,
        isValid: PropTypes.bool,
        value: PropTypes.string.isRequired,
    }).isRequired,
};

export default LoginForm;
